/**
 * AdminPageLevelContext — the level of the Admin page being rendered.
 *
 * RequireAdminPage provides it on every keyed Admin route, after it has
 * refused Hidden pages, so only `editable` and `locked` ever reach a page.
 * Pages and the shared admin blocks read it with `useAdminPageLevel()` to
 * switch into Locked mode.
 *
 * Outside any provider it reads `editable`, so a block rendered anywhere that
 * is not a level-gated Admin route (a patient dialog, a super-admin page)
 * behaves exactly as it always has.
 */
import { createContext, useContext } from 'react';
import type { AdminPageLevel } from '@/services/accountModules.service';

/** A level that lets the page render. Hidden pages never get this far. */
export type ShownAdminPageLevel = Exclude<AdminPageLevel, 'hidden'>;

export const AdminPageLevelContext = createContext<ShownAdminPageLevel>('editable');

export function useAdminPageLevel(): ShownAdminPageLevel {
  return useContext(AdminPageLevelContext);
}
